import { useDndContext, useDroppable } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { cn } from "@repo/utils";
import { DraggableStatusItem } from "~/pages/administration/lists/lists-dialog/status/status-item/draggable-status-item";
import { SortableStatusItem } from "~/pages/administration/lists/lists-dialog/status/status-item/sortable-status-item";
import type { ListStatuses } from "~/types/api";
import { type StatusContainerId, getContainer } from "./dnd";

type Props = {
  id: StatusContainerId;
  title: string;
  emptyPlaceholder: string;
  statuses: ListStatuses[];
  selectedItems: number[];
  toggleSelection: (code: number) => void;
  isSortable?: boolean;
};

const EmptyState = ({ message }: { message: string }) => (
  <div className="py-8 text-center text-sm text-gray-500">{message}</div>
);

export const StatusTable = ({
  id,
  title,
  emptyPlaceholder,
  statuses,
  selectedItems,
  toggleSelection,
  isSortable = false,
}: Props) => {
  const { setNodeRef } = useDroppable({ id, data: { container: id } });
  const { active, over } = useDndContext();

  const isDropTarget = !!active && getContainer(active) !== id && getContainer(over) === id;

  const content =
    statuses.length === 0 ? (
      <EmptyState message={emptyPlaceholder} />
    ) : isSortable ? (
      <SortableContext
        id={id}
        items={statuses.map((s) => s.code)}
        strategy={verticalListSortingStrategy}
      >
        {statuses.map((status) => (
          <SortableStatusItem
            key={status.code}
            container={id}
            status={status}
            isSelected={selectedItems.includes(status.code)}
            onToggle={toggleSelection}
          />
        ))}
      </SortableContext>
    ) : (
      statuses.map((status) => (
        <DraggableStatusItem
          key={status.code}
          container={id}
          status={status}
          isSelected={selectedItems.includes(status.code)}
          onToggle={toggleSelection}
        />
      ))
    );

  return (
    <div className="border-gxe-primary rounded-lg border">
      <div className="space-y-2">
        <div className="border-gxe-primary bg-gxe-secondary flex gap-2 rounded-t-lg border-b p-2 text-sm">
          <h4 className="text-gxe-primary font-medium">{title}</h4>
          <div className="font-semibold text-gray-700">
            {selectedItems.length > 0 ? `(${selectedItems.length})` : ""}
          </div>
        </div>
        <div
          ref={setNodeRef}
          className={cn(
            "max-h-[300px] min-h-[200px] space-y-1 overflow-y-auto rounded-b-lg p-2 transition-colors",
            { "bg-blue-50/60 ring-2 ring-blue-300 ring-inset": isDropTarget }
          )}
        >
          {content}
        </div>
      </div>
    </div>
  );
};
